export * from './exports';

import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import { Payment } from '@/services/payment/types';
import { Invoice } from '@/types/models';
import { formatDate } from '@/lib/utils';
import { formatCurrency } from '@/lib/format';

/**
 * Extended payment type interface for handling payments with additional fields
 */
export interface ExtendedPaymentType extends Omit<Payment, 'family_id'> {
  family_id?: string;
  family_name?: string;
  amount_dollars?: number;
}

/**
 * Utility functions for exporting data to various formats
 */

/**
 * Export data to CSV format
 */
export const exportToCSV = <T extends Record<string, any>>(
  data: T[],
  filename: string,
  headers?: Record<keyof T, string>
) => {
  try {
    // If headers are provided, transform the data to use custom header names
    const transformedData = headers
      ? data.map(item => {
          const transformed: Record<string, any> = {};
          Object.keys(headers).forEach(key => {
            if (key in item) {
              transformed[headers[key as keyof T]] = item[key];
            }
          });
          return transformed;
        })
      : data;

    const csv = Papa.unparse(transformedData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
    return true;
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    return false;
  }
};

/**
 * Export data to Excel format using ExcelJS
 */
export const exportToExcel = async <T extends Record<string, any>>(
  data: T[],
  filename: string,
  headers?: Record<keyof T, string>
): Promise<boolean> => {
  try {
    // Transform data if headers are provided
    const transformedData = headers
      ? data.map(item => {
          const transformed: Record<string, any> = {};
          Object.keys(headers).forEach(key => {
            if (key in item) {
              transformed[headers[key as keyof T]] = item[key];
            }
          });
          return transformed;
        })
      : data;

    if (transformedData.length === 0) {
      throw new Error('No data to export');
    }

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Add headers
    const headerKeys = Object.keys(transformedData[0]);
    worksheet.addRow(headerKeys);

    // Style the header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF4F46E5' } // Indigo color
    };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };

    // Add data rows
    transformedData.forEach(row => {
      worksheet.addRow(Object.values(row));
    });

    // Auto-fit columns with better sizing
    worksheet.columns.forEach((column, index) => {
      if (column.eachCell) {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const cellValue = cell.value ? cell.value.toString() : '';
          if (cellValue.length > maxLength) {
            maxLength = cellValue.length;
          }
        });
        column.width = Math.min(Math.max(maxLength + 2, 12), 50);
      }
    });

    // Add borders to all cells
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    // Generate buffer and save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    saveAs(blob, `${filename}-${new Date().toISOString().split('T')[0]}.xlsx`);
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return false;
  }
};

/**
 * Export data to PDF format
 */
export const exportToPDF = async <T extends Record<string, any>>(
  data: T[],
  filename: string,
  headers?: Record<keyof T, string>,
  title?: string
) => {
  try {
    const jsPDFModule = await import('jspdf');
    const autoTableModule = await import('jspdf-autotable');
    const jsPDF = jsPDFModule.default;
    const autoTable = autoTableModule.default;
    const doc = new jsPDF();
    
    // Add title if provided
    if (title) {
      doc.setFontSize(16);
      doc.text(title, 14, 15);
      doc.setFontSize(10);
    }

    // Transform data for PDF table
    const transformedData = headers
      ? data.map(item => 
          Object.keys(headers).map(key => 
            item[key]?.toString() || ''
          )
        )
      : data.map(item => Object.values(item).map(val => val?.toString() || ''));

    // Generate PDF table
    autoTable(doc, {
      head: [headers ? Object.values(headers) : Object.keys(data[0] || {})],
      body: transformedData,
      startY: title ? 25 : 15,
      margin: { horizontal: 14 },
      styles: { fontSize: 8 },
      headStyles: { fillColor: [75, 75, 75] }
    });

    doc.save(`${filename}-${new Date().toISOString().split('T')[0]}.pdf`);
    return true;
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    return false;
  }
};

/**
 * Export payments to CSV
 * Now supports both Payment and extended Payment types
 */
export const exportPaymentsToCSV = (
  payments: (Payment | ExtendedPaymentType)[], 
  organizationName: string = 'Your Studio'
) => {
  // Map payment data to a more readable format
  const formattedPayments = payments.map(payment => ({
    id: payment.id,
    amount: payment.amount_cents ? formatCurrency(payment.amount_cents / 100) : '$0.00',
    date: payment.paid_at ? formatDate(payment.paid_at) : 'N/A',
    status: payment.status || 'Unknown',
    family: 'family_name' in payment ? payment.family_name : (payment.family_id || 'N/A'),
    payment_method: payment.stripe_payment_intent ? 'Credit Card' : 'Other',
    receipt: payment.receipt_url ? 'Available' : 'Not Available'
  }));

  // Define CSV headers
  const headers = {
    id: 'Payment ID',
    amount: 'Amount',
    date: 'Payment Date',
    status: 'Status',
    family: 'Family',
    payment_method: 'Payment Method',
    receipt: 'Receipt'
  };

  return exportToCSV(formattedPayments, `${organizationName}-Payments`, headers);
};

/**
 * Export invoices to CSV
 */
export const exportInvoicesToCSV = (invoices: Invoice[], families: Record<string, string>, organizationName: string = 'Your Studio') => {
  // Map invoice data to a more readable format
  const formattedInvoices = invoices.map(invoice => ({
    id: invoice.id,
    family: families[invoice.family_id] || 'Unknown Family',
    amount: formatCurrency(invoice.total_cents / 100),
    due_date: formatDate(invoice.due_date),
    status: invoice.status,
    created_at: formatDate(invoice.created_at)
  }));

  // Define CSV headers
  const headers = {
    id: 'Invoice ID',
    family: 'Family Name',
    amount: 'Amount',
    due_date: 'Due Date',
    status: 'Status',
    created_at: 'Created Date'
  };

  return exportToCSV(formattedInvoices, `${organizationName}-Invoices`, headers);
};

/**
 * Export financial data to Excel
 */
export const exportFinancialDataToExcel = async (data: any[], organizationName: string): Promise<boolean> => {
  const headers = {
    month: "Month",
    revenue: "Revenue ($)",
    expenses: "Expenses ($)",
    profit: "Profit ($)",
    enrollments: "Enrollments",
    classCount: "Classes"
  };
  return await exportToExcel(data, `${organizationName}-Financial-Summary`, headers);
};

/**
 * Export financial data to PDF
 */
export const exportFinancialDataToPDF = (data: any[], organizationName: string) => {
  const headers = {
    month: "Month",
    revenue: "Revenue ($)",
    expenses: "Expenses ($)",
    profit: "Profit ($)",
    enrollments: "Enrollments",
    classCount: "Classes"
  };
  return exportToPDF(data, `${organizationName}-Financial-Summary`, headers, "Financial Summary Report");
};
